import { useToast } from '@chakra-ui/react';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { Subject } from 'rxjs';
import { match } from 'ts-pattern';
import { serverUrl } from '../../config';
import { useAuthenticateApiCall } from './useAuthenticateApiCall';
import { useSubscription } from './useSubscription';
import { useQueryClient } from '@tanstack/react-query';
import { queries } from '../../data/queries';

const mkAcceptInvitation = (token: string) => (invitationId: string) => {
  const fetchUrl = `${serverUrl}/api/invitations/${invitationId}/accept`;
  const request = { method: 'get', headers: { authorization: `Token ${token}` } };
  return fetch(fetchUrl, request).then((res) => res.json());
};

type InvitationEvent =
  | { type: 'setInvitation'; invitationId: string }
  | { type: 'acceptInvitation' }
  | { type: 'dismissInvitation' };

const bus$Atom = atom(new Subject<InvitationEvent>());
const invitationIdAtom = atom<string | undefined>(undefined);

export const useInvitationsHandler = () => {
  const bus$ = useAtomValue(bus$Atom);
  const [invitationId, setInvitationId] = useAtom(invitationIdAtom);

  const acceptInvitation = useAuthenticateApiCall(mkAcceptInvitation);
  const toast = useToast();

  const queryClient = useQueryClient();

  useSubscription(
    () =>
      bus$.subscribe((event) => {
        match(event)
          .with({ type: 'setInvitation' }, ({ invitationId }) => {
            setInvitationId(invitationId);
          })
          .with({ type: 'acceptInvitation' }, () => {
            if (!invitationId) return;

            acceptInvitation(invitationId).then((res: any) => {
              if (res.status === 'error') {
                toast({ title: res.message, position: 'top' });
              } else {
                toast({ title: 'Invitation Accepted', position: 'top' });
                setInvitationId(undefined);
                queryClient.invalidateQueries({
                  queryKey: [queries.writingDesk],
                });
              }
            });
          })
          .with({ type: 'dismissInvitation' }, () => {
            setInvitationId(undefined);
          })
          .exhaustive();
      }),
    [bus$, invitationId, setInvitationId, acceptInvitation, queryClient, toast]
  );
};

export const useInvitations = () => {
  const bus$ = useAtomValue(bus$Atom);
  const invitationId = useAtomValue(invitationIdAtom);

  const dispatch = useCallback((event: InvitationEvent) => bus$.next(event), [bus$]);

  return { invitationId, dispatch } as const;
};
